import {
	DetailedNetBenefitTable,
	RoomAvailabilityTable,
} from '@/features/group-pricing/components';
import {
	AdrValidatedRateField,
	GroupPricingDateField,
	GroupPricingNumberField,
	GroupPricingSlider,
	GroupPricingTextField,
} from '@/features/group-pricing/components/inputs';
import { useEffect, useMemo, useState } from 'react';

import { Form } from '@/components/ui/form';
import { usePropertyContext } from '@/context/propertyContext';
import { TakeGroupCard } from '@/features/group-pricing/components';
import { CalculationsSummary } from '@/features/group-pricing/components/calculations-summary';
import { DeleteConfirmationDialog } from '@/features/group-pricing/components/dialogs/delete-confirmation-dialog';
import { LoadGroupDialog } from '@/features/group-pricing/components/dialogs/load-group-dialog';
import { ResetOptionsDialog } from '@/features/group-pricing/components/dialogs/reset-options-dialog';
import { useGroupPricing } from '@/features/group-pricing/hooks/use-group-pricing';
import {
	type FormSchema,
	createFormSchema,
} from '@/features/group-pricing/zod';
import { labels } from '@/locales/en.label';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import type { z } from 'zod';
import { dateOnly } from '../../date-utils';
import { AdvancedForm } from './advanced-form';
import { FormSubmitBar } from './form-submit-bar';

export function GroupPricingForm() {
	const {
		activeGroup,
		defaultFormValues: defaultValues,
		savedGroups,
		saveGroup,
		updateGroup,
		deleteGroup,
		setActiveGroup,
		loading,
	} = useGroupPricing();

	// UI state
	const [showAdvanced, setShowAdvanced] = useState(false);
	const [loadDialogOpen, setLoadDialogOpen] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [resetDialogOpen, setResetDialogOpen] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const { property } = usePropertyContext();
	const schema = useMemo(
		() => createFormSchema(property?.physicalCap),
		[property?.physicalCap],
	);
	const form = useForm<z.infer<typeof FormSchema>>({
		resolver: zodResolver(schema),
		defaultValues,
		mode: 'onChange', // Enable real-time validation
	});

	useEffect(() => {
		// Reset form to default when property changes without saving current changes.
		if (form.formState.isDirty && property) {
			form.reset(defaultValues);
		}
	}, [defaultValues, property]);

	// Show reset options dialog
	const handleResetClick = () => {
		setResetDialogOpen(true);
	};

	// Reset to active group's saved state
	const handleResetToSaved = () => {
		if (activeGroup) {
			form.reset({
				...activeGroup,
				// Extract just the date portion to avoid timezone issues
				check_in: dateOnly(activeGroup.check_in),
				check_out: dateOnly(activeGroup.check_out),
			});
		}
	};

	// Reset to default values regardless of active group
	const handleResetToDefault = () => {
		// Clear the active group so we're working with a fresh form
		setActiveGroup(null);

		// Reset form to default values
		form.reset({
			...defaultValues,
			check_in: dateOnly(defaultValues.check_in),
			check_out: dateOnly(defaultValues.check_out),
		});
	};

	const toggleAdvanced = () => {
		setShowAdvanced((prev) => !prev);
	};

	// Update form when active group changes
	const resetToActiveGroup = () => {
		if (activeGroup) {
			handleResetToSaved();
		} else {
			handleResetToDefault();
		}
	};

	useEffect(() => {
		resetToActiveGroup();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeGroup]);

	async function onSubmit(values: z.infer<typeof FormSchema>) {
		try {
			// Save the form data to the API
			if (activeGroup?.id) {
				// If we have an active group, update it
				await updateGroup(activeGroup.id, values);
			} else {
				// Otherwise create a new group
				await saveGroup(values);
			}
			toast.success(`Group ${values?.group_name} saved successfully!`);
		} catch (error) {
			console.error('Error submitting form:', error);
			toast.error('Error saving group. Please try again.');
		}
	}

	function onError(error: unknown) {
		console.error('Form validation errors:', error);
	}

	// Show load dialog when user clicks load
	const handleLoad = () => {
		setLoadDialogOpen(true);
	};

	// Handle group selection from the load dialog
	const handleSelectGroup = (groupId: string) => {
		setActiveGroup(groupId);
		setLoadDialogOpen(false);
	};

	// Show delete confirmation when user clicks delete
	const handleDeleteClick = () => {
		if (activeGroup?.id) {
			setDeleteDialogOpen(true);
		} else {
			console.log('No active group to delete');
		}
	};

	// Handle delete confirmation
	const handleDeleteConfirm = async () => {
		if (!activeGroup?.id) return;

		setIsDeleting(true);
		try {
			const groupName = activeGroup.group_name;
			await deleteGroup(activeGroup.id);
			setDeleteDialogOpen(false);
			toast.success(`Group ${groupName} deleted successfully!`);
		} catch (error) {
			console.error('Error deleting group:', error);
			toast.error('Error deleting group. Please try again.');
		} finally {
			setIsDeleting(false);
		}
	};
	const groupNameValue = form.watch('group_name');
	const isSaveGroupButtonDisabled =
		!groupNameValue || groupNameValue.trim().length === 0;
	return (
		<Form {...form}>
			<TakeGroupCard />

			<form
				onSubmit={form.handleSubmit(onSubmit, onError)}
				className='space-y-8'
			>
				{/* Top Form Section */}
				<div className='tw-flex tw-justify-between tw-my-4'>
					<div className='tw-w-full'>
						<GroupPricingTextField
							name='group_name'
							placeholder='Group Name'
							description='What is the name of the group?'
						/>
					</div>

					<div className='tw-ml-2'>
						<FormSubmitBar
							handleReset={handleResetClick}
							showAdvanced={showAdvanced}
							toggleAdvanced={toggleAdvanced}
							onLoad={handleLoad}
							onDelete={handleDeleteClick}
							isSaveGroupButtonDisabled={isSaveGroupButtonDisabled}
						/>
					</div>
				</div>

				{/* Two Column Layout: Form Fields and Calculations Summary */}
				<div className='tw-grid tw-grid-cols-1 tw-gap-8 lg:tw-grid-cols-3'>
					{/* Left Column: Form Fields */}
					<div className='tw-col-span-1 lg:tw-col-span-2'>
						<div className='tw-grid tw-grid-cols-1 tw-gap-6 md:tw-grid-cols-2 lg:tw-grid-cols-3'>
							{/* Check In Date */}
							<GroupPricingDateField
								name='check_in'
								description='The first day of the group.'
							/>

							{/* Check Out Date */}
							<GroupPricingDateField
								name='check_out'
								description='The last day of the group.'
							/>

							{/* Rooms Requested */}
							<GroupPricingNumberField
								name='rooms_requested'
								min={1}
								max={property?.physicalCap}
								description='Daily rooms for the group.'
							/>

							{/* Rate Requested */}
							<AdrValidatedRateField
								name='rate_requested'
								min={1}
								step={1}
								description='Group rate.'
								symbol={{ prefix: '$' }}
							/>

							{/* Commission */}
							<GroupPricingSlider
								name='commission'
								description='Commission to be paid.'
								symbol={{ suffix: '%' }}
							/>

							{/* Profit Margin */}
							<GroupPricingSlider
								name='profit_margin'
								description='Required Profit Margin.'
								tooltip={labels.group_pricing.profit_margin_tooltip}
								symbol={{ suffix: '%' }}
							/>
						</div>

						{/* Advanced Form Components */}
						{showAdvanced && (
							<div className='tw-mt-6'>
								<AdvancedForm />
							</div>
						)}
					</div>

					{/* Right Column: Calculations Summary */}
					<div className='tw-col-span-1'>
						<CalculationsSummary className='tw-sticky tw-top-4' />
					</div>
				</div>
			</form>

			{/* Data Tables */}
			<div className='tw-mt-8'>
				<DetailedNetBenefitTable />
				<RoomAvailabilityTable />
			</div>

			{/* Dialogs */}
			<LoadGroupDialog
				open={loadDialogOpen}
				onOpenChange={setLoadDialogOpen}
				groups={savedGroups.filter(
					(group): group is NonNullable<typeof group> =>
						group !== undefined && group !== null,
				)}
				onSelectGroup={handleSelectGroup}
				isLoading={loading}
			/>

			<DeleteConfirmationDialog
				open={deleteDialogOpen}
				onOpenChange={setDeleteDialogOpen}
				group={activeGroup}
				onConfirm={handleDeleteConfirm}
				isLoading={isDeleting}
			/>

			<ResetOptionsDialog
				open={resetDialogOpen}
				onOpenChange={setResetDialogOpen}
				onResetToDefault={handleResetToDefault}
				onResetToSaved={handleResetToSaved}
				activeGroup={activeGroup}
			/>
		</Form>
	);
}
